import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useUIStore } from '../../store/uiStore';
import { ChangePassword } from './ChangePassword';

const SUPPORTED_LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी (Hindi)' },
  { value: 'mr', label: 'मराठी (Marathi)' },
];

export const UserPreferences: React.FC = () => {
  const { theme, locale, setTheme, setLocale } = useUIStore();

  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system'>(
    theme === 'light' || theme === 'dark' ? theme : 'system'
  );
  const [localePreference, setLocalePreference] = useState(locale);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);

  useEffect(() => {
    // Apply system theme preference if selected
    if (themePreference === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    } else {
      setTheme(themePreference);
    }
  }, [themePreference, setTheme]);

  const handleSavePreferences = () => {
    // Apply theme
    if (themePreference !== 'system') {
      setTheme(themePreference);
    }

    // Apply locale
    setLocale(localePreference);

    // In a real implementation, we would save notification preferences to the user profile
    // For now, we just store them in local state
    console.log('Saving preferences:', {
      theme: themePreference,
      locale: localePreference,
      notificationsEnabled,
      emailNotifications,
      pushNotifications,
    });
  };

  return (
    <Stack spacing={3}>
      {/* Appearance */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appearance
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Customize how the application looks.
          </Typography>

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Theme</FormLabel>
            <RadioGroup
              value={themePreference}
              onChange={(e) => setThemePreference(e.target.value as 'light' | 'dark' | 'system')}
            >
              <FormControlLabel value="light" control={<Radio />} label="Light" />
              <FormControlLabel value="dark" control={<Radio />} label="Dark" />
              <FormControlLabel
                value="system"
                control={<Radio />}
                label="System (follows your device settings)"
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Language & Region */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Language & Region
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Set your preferred language and regional settings.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Language</FormLabel>
            <Select
              value={localePreference}
              onChange={(e) => setLocalePreference(e.target.value)}
              sx={{ mt: 1 }}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <MenuItem key={locale.value} value={locale.value}>
                  {locale.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage how you receive notifications.
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                />
              }
              label="Enable notifications"
            />

            <Box sx={{ pl: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    disabled={!notificationsEnabled}
                  />
                }
                label="Email notifications"
              />
            </Box>

            <Box sx={{ pl: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                    disabled={!notificationsEnabled}
                  />
                }
                label="Push notifications"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSavePreferences}>
          Save Preferences
        </Button>
      </Box>

      {/* Change Password */}
      <ChangePassword />
    </Stack>
  );
};
